import React, { useMemo } from 'react';
import type { DayOfWeek, Ingredient, Language, MealType, PlannedMeal, Recipe } from '../types';
import ActiveCookingSessionScreen from './ActiveCookingSessionScreen';
import { useCookingSessionDocument } from '../hooks/useCookingSessionDocument';

interface PlannedMealRecipeViewProps {
    recipe: Recipe;
    plannedServings: number;
    day: DayOfWeek;
    mealType: MealType;
    weekOffset: number;
    activeHouseholdId?: string;
    orderedDays?: DayOfWeek[];
    language?: Language;
    onClose: (servings: number) => void;
    onAddIngredientsToCart?: (ingredients: Ingredient[], recipeName: string) => Promise<void>;
}

function getSessionDateString(
    day: DayOfWeek,
    orderedDays: DayOfWeek[] | undefined,
    weekOffset: number
): string {
    if (!orderedDays || orderedDays.length === 0) return '';

    const startDayOfWeek = orderedDays[0];
    const now = new Date();
    const currentDayIndex = now.getDay();
    const startDayIdx = startDayOfWeek === 'Sunday' ? 0 : 1;
    let diff = currentDayIndex - startDayIdx;
    if (startDayOfWeek === 'Monday' && currentDayIndex === 0) diff = 6;
    else if (diff < 0) diff += 7;

    const currentWeekStart = new Date(now);
    currentWeekStart.setDate(now.getDate() - diff);
    currentWeekStart.setHours(0, 0, 0, 0);

    const dayIndex = orderedDays.indexOf(day);
    if (dayIndex === -1) return '';

    const targetDate = new Date(currentWeekStart);
    targetDate.setDate(currentWeekStart.getDate() + (weekOffset * 7) + dayIndex);

    const yyyy = targetDate.getFullYear();
    const mm = String(targetDate.getMonth() + 1).padStart(2, '0');
    const dd = String(targetDate.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

const PlannedMealRecipeView: React.FC<PlannedMealRecipeViewProps> = ({
    recipe,
    plannedServings,
    day,
    mealType,
    weekOffset,
    activeHouseholdId,
    orderedDays,
    language,
    onClose,
    onAddIngredientsToCart,
}) => {
    const defaultServingsByRecipeId = useMemo(
        () => ({ [recipe.id]: plannedServings }),
        [recipe.id, plannedServings]
    );

    const {
        struckIngredients,
        completedSteps,
        servingsMap,
        toggleStrike,
        toggleStepComplete,
        updateRecipeServings,
        markRecipeDone,
        isRecipeDone,
    } = useCookingSessionDocument({
        activeHouseholdId,
        weekOffset,
        day,
        mealType,
        defaultServingsByRecipeId,
        language,
    });

    const servings = servingsMap[recipe.id] ?? plannedServings;
    const sessionDateString = useMemo(
        () => getSessionDateString(day, orderedDays, weekOffset),
        [day, orderedDays, weekOffset]
    );

    const mealRecipe = useMemo((): PlannedMeal & { recipe: Recipe } => ({
        id: 'planned-meal-preview',
        recipeId: recipe.id,
        plannedServings: servings,
        recipe,
    }), [recipe, servings]);

    return (
        <ActiveCookingSessionScreen
            mealRecipe={mealRecipe}
            language={language}
            servings={servings}
            onUpdateServings={(nextServings) => {
                void updateRecipeServings(recipe.id, nextServings);
            }}
            struckIngredients={struckIngredients}
            onToggleStruck={(key) => {
                void toggleStrike(key);
            }}
            completedSteps={completedSteps}
            onToggleStepComplete={(key) => {
                void toggleStepComplete(key);
            }}
            isRecipeDone={isRecipeDone(recipe.id, recipe)}
            onMarkRecipeDone={() => {
                void markRecipeDone(recipe.id, recipe);
            }}
            onBack={() => onClose(servings)}
            onAddIngredientsToCart={onAddIngredientsToCart}
            sessionDateString={sessionDateString}
            showMealServingsContext
        />
    );
};

export default PlannedMealRecipeView;
